import React, { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck, Link2 } from "lucide-react";
import api from "../../../lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

// Incorrect claims about CASS, kept separate from visibility work because the
// response to them is different: correct the source, not the marketing.
export default function AccuracyRisks() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/ai-monitor/accuracy").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="ai-accuracy-risks">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-accuracy-summary">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Accurate answers</div>
          <div className="text-2xl font-bold text-navy mt-1">
            {data.accuracy_rate === null ? "—" : `${(data.accuracy_rate * 100).toFixed(0)}%`}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {data.assessable ? `${data.accurate} of ${data.assessable} assessable answers` : "Nothing assessable yet"}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-accuracy-correctable">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Worth correcting</div>
          <div className="text-2xl font-bold text-navy mt-1">
            {data.risks.filter((r) => r.influence === "content").length}
          </div>
          <div className="text-xs text-slate-500 mt-1">Repeated or cited, so traceable to a source</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-accuracy-isolated">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Isolated errors</div>
          <div className="text-2xl font-bold text-navy mt-1">
            {data.risks.filter((r) => r.influence === "isolated").length}
          </div>
          <div className="text-xs text-slate-500 mt-1">One platform, nothing cited — recorded, not actioned</div>
        </div>
      </div>

      <p className="text-xs text-slate-500">{data.note}</p>

      {data.risks.length === 0 ? (
        <p className="text-sm text-slate-600" data-testid="ai-accuracy-empty">
          Nothing incorrect has been recorded. The five branded questions are what test this, so this stays empty
          until they have been asked.
        </p>
      ) : (
        <div className="space-y-3">
          {data.risks.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ai-risk-${r.id}`}>
              <div className="flex flex-wrap items-start gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                  r.influence === "content" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                  {r.influence === "content" ? "high priority · correctable" : "low priority · isolated error"}
                </span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                  {r.accuracy.replace("_", " ")}
                </span>
                <span className="text-sm font-bold text-navy flex-1 min-w-[200px]">{r.platform}</span>
                <button onClick={() => setOpen(open === r.id ? null : r.id)}
                        className="text-xs font-semibold text-slate-500 hover:text-navy"
                        data-testid={`ai-risk-toggle-${r.id}`}>
                  {open === r.id ? "Hide the answer" : "Read the original answer"}
                </button>
              </div>

              <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                {r.claim || "The answer did not match the verified business facts."}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Asked “{r.question}” · {when(r.asked_at)}
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                {r.repeated_on.length > 0 ? (
                  <span className="inline-flex items-center gap-1.5 text-red-600" data-testid={`ai-risk-repeated-${r.id}`}>
                    <AlertTriangle size={12} /> Also said by {r.repeated_on.join(", ")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-slate-500">
                    <ShieldCheck size={12} /> Not seen on another platform
                  </span>
                )}
                {(r.citations || []).length > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <Link2 size={12} /> {r.citations.length} source{r.citations.length === 1 ? "" : "s"} cited
                  </span>
                )}
              </div>

              {open === r.id && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="text-xs font-bold text-navy">The answer, as given</p>
                  <pre className="mt-1 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs text-slate-700 max-h-80 overflow-y-auto">
                    {r.response_text}
                  </pre>
                  {(r.citations || []).length > 0 && (
                    <div className="mt-2 text-[11px] text-slate-600">
                      <strong>Cited:</strong>{" "}
                      {r.citations.map((c) => c.title || c.url).join(", ")}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-2">
                    Kept in full as evidence, whatever is later decided about the classification.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
